import React, { useEffect, useState } from 'react';
import { ScrollText, ChevronDown, ChevronUp } from 'lucide-react';
import { listAuditLogs } from '../services/systemAuditLogService';

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  statusChange: 'bg-yellow-100 text-yellow-700',
  addMember: 'bg-purple-100 text-purple-700',
  removeMember: 'bg-pink-100 text-pink-700',
};

export default function AuditLogsPage() {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ collection: '', action: '', userId: '' });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const limit = 20;

  const load = () => {
    setLoading(true);
    listAuditLogs({
      collection: filters.collection || undefined,
      action: filters.action || undefined,
      userId: filters.userId || undefined,
      page,
      limit,
    })
      .then((res) => {
        setLogs(res.data || []);
        setTotal(res.total || 0);
      })
      .catch(() => { setLogs([]); setTotal(0); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [page]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="theme-grid p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="text-gray-500 mt-1">Track all data changes across system collections</p>
      </div>

      <form onSubmit={handleFilter} className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Collection</label>
          <select value={filters.collection} onChange={(e) => setFilters({ ...filters, collection: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
            <option value="">All</option>
            <option value="organizations">organizations</option>
            <option value="tenants">tenants</option>
            <option value="users">users</option>
            <option value="systemRoles">systemRoles</option>
            <option value="organizationRoles">organizationRoles</option>
            <option value="lookups">lookups</option>
            <option value="lookupGroups">lookupGroups</option>
            <option value="userGroups">userGroups</option>
            <option value="tiers">tiers</option>
            <option value="featureFlags">featureFlags</option>
            <option value="tenantFeatureFlags">tenantFeatureFlags</option>
            <option value="modules">modules</option>
            <option value="tenantModules">tenantModules</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
          <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
            <option value="">All</option>
            <option value="create">create</option>
            <option value="update">update</option>
            <option value="delete">delete</option>
            <option value="statusChange">statusChange</option>
            <option value="addMember">addMember</option>
            <option value="removeMember">removeMember</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">User ID</label>
          <input type="text" value={filters.userId} onChange={(e) => setFilters({ ...filters, userId: e.target.value })} placeholder="Filter by user" className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 w-48" />
        </div>
        <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700">Filter</button>
        <button type="button" onClick={() => { setFilters({ collection: '', action: '', userId: '' }); setPage(1); setTimeout(load, 0); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">Clear</button>
        <div className="ml-auto text-sm text-gray-500">{total} total entries</div>
      </form>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <ScrollText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No audit logs found</p>
          <p className="text-sm text-gray-400 mt-1">Perform CRUD operations to generate audit entries</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Collection</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Summary</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <React.Fragment key={log._id}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedId(expandedId === log._id ? null : log._id)}>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{new Date(log.createdAt).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-700">{log.collection}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600'}`}>{log.action}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{log.summary}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{log.performedBy?.name || log.performedBy?.email || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        {expandedId === log._id ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      </td>
                    </tr>
                    {expandedId === log._id && (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-1">Document ID</h4>
                              <p className="font-mono text-gray-500">{log.documentId}</p>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-1">Request</h4>
                              <p className="text-gray-500">{log.request?.method} {log.request?.path}</p>
                              <p className="text-gray-400">IP: {log.request?.ip}</p>
                            </div>
                            {log.before && (
                              <div>
                                <h4 className="font-semibold text-gray-700 mb-1">Before</h4>
                                <pre className="bg-white border border-gray-200 rounded p-2 overflow-auto max-h-40 text-gray-600">{JSON.stringify(log.before, null, 2)}</pre>
                              </div>
                            )}
                            {log.after && (
                              <div>
                                <h4 className="font-semibold text-gray-700 mb-1">After</h4>
                                <pre className="bg-white border border-gray-200 rounded p-2 overflow-auto max-h-40 text-gray-600">{JSON.stringify(log.after, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50">Previous</button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50">Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
